import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Send, Loader2, Shield, CheckCircle, Calendar } from 'lucide-react';
import { Textarea } from '../ui/textarea';

export function QuickSessionCard({ professional, user, onSubmit }) {
  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !question.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(question);
      setSuccess(true);
      setShowForm(false);
      setQuestion('');
    } catch (_) {} finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card-theraflow p-6" data-testid="quick-session-card">
      <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        <Zap className="w-5 h-5 inline-block mr-2 text-[#D4A373]" strokeWidth={1.5} />
        Sessão Rápida
      </h2>
      <p className="text-sm text-[#63635E] mb-4">Tire uma dúvida rápida em até 15 minutos por um valor reduzido.</p>

      {success ? (
        <div className="p-4 rounded-xl bg-[#6B8E7C]/10 text-[#6B8E7C] text-sm mb-4">
          Solicitação enviada! O profissional responderá em breve.
        </div>
      ) : showForm && user ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Descreva brevemente sua dúvida..." className="input-theraflow min-h-[100px]" data-testid="quick-session-question" />
          <div className="flex gap-2">
            <button type="submit" disabled={submitting || !question.trim()} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="submit-quick-session">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" strokeWidth={1.5} /> Enviar</>}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
          </div>
        </form>
      ) : (
        <button onClick={() => user && setShowForm(true)} className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="start-quick-session">
          <Zap className="w-4 h-4" strokeWidth={1.5} />
          {professional.quick_session_price ? `R$ ${professional.quick_session_price?.toFixed(0)} - Tirar Dúvida` : 'Tirar Dúvida Rápida'}
        </button>
      )}

      {!user && <p className="mt-3 text-xs text-[#A0A09A] text-center">Faça login para solicitar uma sessão</p>}
    </div>
  );
}

export function VerificationCard({ professional }) {
  return (
    <div className="card-theraflow p-6" data-testid="verification-status-card">
      <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        <Shield className="w-5 h-5 inline-block mr-2" strokeWidth={1.5} />
        Status de Verificação
      </h2>
      {professional.verified ? (
        <div className="flex items-start gap-3 p-4 bg-[#6B8E7C]/10 rounded-xl">
          <CheckCircle className="w-5 h-5 text-[#6B8E7C] flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-medium text-[#6B8E7C]">Verificado pela Comunidade</p>
            <p className="text-xs text-[#63635E] mt-1">{professional.rating_count}+ avaliações positivas</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#63635E]">Avaliações</span>
            <span className="font-medium text-[#292927]">{professional.rating_count || 0}/10</span>
          </div>
          <div className="w-full bg-[#EAE8E3] rounded-full h-2">
            <div className="bg-[#4A5D4E] h-2 rounded-full transition-all" style={{ width: `${Math.min((professional.rating_count || 0) / 10 * 100, 100)}%` }} />
          </div>
          <p className="text-xs text-[#A0A09A]">Profissionais são verificados após 10 avaliações com média ≥ 4.0</p>
        </div>
      )}
    </div>
  );
}

export function AvailabilityCard({ professional, professionalId }) {
  return (
    <div className="card-theraflow p-6 sticky top-24" data-testid="availability-card">
      <h2 className="text-lg font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        <Calendar className="w-5 h-5 inline-block mr-2" strokeWidth={1.5} />
        Disponibilidade
      </h2>
      {professional.availability?.length > 0 ? (
        <div className="space-y-4">
          {professional.availability.map((day) => (
            <div key={day.day} className="pb-4 border-b border-[#EAE8E3] last:border-0 last:pb-0">
              <p className="font-medium text-[#292927] mb-2">{day.day}</p>
              <div className="flex flex-wrap gap-2">
                {day.slots.map((slot) => (
                  <span key={slot} className="px-3 py-1 bg-[#F6EBE6] text-[#63635E] text-sm rounded-full">{slot}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#A0A09A] text-sm">Entre em contato para verificar disponibilidade</p>
      )}
      <div className="mt-6 pt-6 border-t border-[#EAE8E3]">
        <Link to={`/session/book/${professionalId}`} className="btn-primary w-full flex items-center justify-center gap-2 mb-3" data-testid="book-session-button">
          <Calendar className="w-4 h-4" strokeWidth={1.5} /> Agendar Sessão
        </Link>
        <p className="text-xs text-[#A0A09A] text-center">A partir de R$ {professional.hourly_rate?.toFixed(0) || '150'}/hora</p>
      </div>
    </div>
  );
}
